import type { z } from 'zod';

/**
 * An event a module publishes for others, e.g. `hr.absence.approved`. The publishing module
 * exports the definition from its public API, subscribers import it from there
 * (docs/architecture/modules.md, Events).
 *
 * Payloads carry IDs and technical values, never names, emails or other personal data in clear
 * text: subscribers fetch what they need through the public API of the publisher.
 */
export interface EventDefinition<T = unknown> {
  /** `<module>.<entity>.<verb>`, the module ID first. */
  type: string;
  schema: z.ZodType<T>;
}

export type EventPayload<E> = E extends EventDefinition<infer T> ? T : never;

const EVENT_TYPE = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){2,3}$/;

export function isEventType(value: string): boolean {
  return EVENT_TYPE.test(value);
}

export function defineEvent<T>(type: string, schema: z.ZodType<T>): EventDefinition<T> {
  if (!isEventType(type)) {
    throw new Error(`Event type "${type}" must look like "<module>.<entity>.<verb>"`);
  }
  return { type, schema };
}
